import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ZoomIn, Plus, Minus, RotateCcw, X, ChevronLeft, ChevronRight, ImageOff } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";

/**
 * Lightweight product gallery. Shows EVERY image individually (never a collage).
 * - Desktop: large image + prev/next arrows + thumbnail strip.
 * - Mobile: native swipe (embla) + "n / total" counter + dots.
 * - Click/tap the image -> zoom lightbox with +/- zoom, reset, pan & keyboard nav.
 * Reuses the existing embla Carousel; no new dependencies.
 */
export function ProductGallery({ images = [], alt = "", badge = null, testid = "product-gallery" }) {
  const pics = useMemo(() => Array.from(new Set((images || []).filter(Boolean))), [images]);
  const [api, setApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [zoom, setZoom] = useState(false);

  useEffect(() => {
    if (!api) return;
    const onSel = () => setSelected(api.selectedScrollSnap());
    api.on("select", onSel); onSel();
    return () => { try { api.off("select", onSel); } catch { /* noop */ } };
  }, [api]);

  if (pics.length === 0) {
    return (
      <div data-testid={testid}>
        <div className="grid aspect-square w-full place-items-center rounded-wp-xl border border-border bg-wp-cream-100 text-wp-ink-muted shadow-wp-card">
          <ImageOff className="h-8 w-8" />
        </div>
      </div>
    );
  }

  const multi = pics.length > 1;

  return (
    <div data-testid={testid}>
      <div className="group relative overflow-hidden rounded-wp-xl border border-border bg-wp-cream-100 shadow-wp-card">
        <Carousel setApi={setApi} opts={{ loop: multi, align: "start" }}>
          <CarouselContent>
            {pics.map((src, i) => (
              <CarouselItem key={src + i}>
                <button type="button" onClick={() => setZoom(true)} aria-label="Zoom image"
                  className="block aspect-square w-full cursor-zoom-in" data-testid={`gallery-slide-${i}`}>
                  <img src={src} alt={alt} loading={i === 0 ? "eager" : "lazy"} draggable={false}
                    className="h-full w-full select-none object-cover" />
                </button>
              </CarouselItem>
            ))}
          </CarouselContent>
          {multi && (
            <>
              <CarouselPrevious className="left-3 hidden border-0 bg-white/85 text-wp-ink shadow-wp-soft hover:bg-white sm:flex" data-testid="gallery-prev" />
              <CarouselNext className="right-3 hidden border-0 bg-white/85 text-wp-ink shadow-wp-soft hover:bg-white sm:flex" data-testid="gallery-next" />
            </>
          )}
        </Carousel>

        {badge && selected === 0 && <div className="pointer-events-none absolute left-3 top-3">{badge}</div>}

        <span className="pointer-events-none absolute right-3 top-3 rounded-full bg-wp-ink/45 p-1.5 text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
          <ZoomIn className="h-4 w-4" />
        </span>

        {multi && (
          <span className="pointer-events-none absolute bottom-3 right-3 rounded-full bg-wp-ink/60 px-2.5 py-1 text-xs font-medium text-white" data-testid="gallery-counter">
            {selected + 1} / {pics.length}
          </span>
        )}
        {multi && pics.length <= 8 && (
          <div className="pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5 sm:hidden">
            {pics.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all duration-200 ${selected === i ? "w-4 bg-white" : "w-1.5 bg-white/60"}`} />
            ))}
          </div>
        )}
      </div>

      {multi && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
          {pics.map((src, i) => (
            <button key={src + i} type="button" onClick={() => api?.scrollTo(i)}
              className={`h-16 w-16 flex-shrink-0 overflow-hidden rounded-wp-sm border-2 transition ${selected === i ? "border-wp-rose-strong" : "border-transparent opacity-70 hover:opacity-100"}`}
              data-testid={`gallery-thumb-${i}`}>
              <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {zoom && <Lightbox pics={pics} index={selected} onIndex={(i) => api?.scrollTo(i)} onClose={() => setZoom(false)} alt={alt} />}
    </div>
  );
}

function Lightbox({ pics, index, onIndex, onClose, alt }) {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  useEffect(() => { setScale(1); setPos({ x: 0, y: 0 }); }, [index]);
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onIndex((index + 1) % pics.length);
      if (e.key === "ArrowLeft") onIndex((index - 1 + pics.length) % pics.length);
    };
    window.addEventListener("keydown", h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", h); document.body.style.overflow = prev; };
  }, [index, pics.length, onClose, onIndex]);

  const zoomIn = () => setScale((s) => Math.min(3, +(s + 0.5).toFixed(2)));
  const zoomOut = () => setScale((s) => { const n = Math.max(1, +(s - 0.5).toFixed(2)); if (n === 1) setPos({ x: 0, y: 0 }); return n; });
  const reset = () => { setScale(1); setPos({ x: 0, y: 0 }); };

  const start = (x, y) => { if (scale > 1) drag.current = { x, y, ox: pos.x, oy: pos.y }; };
  const move = (x, y) => { if (drag.current) setPos({ x: drag.current.ox + (x - drag.current.x), y: drag.current.oy + (y - drag.current.y) }); };
  const end = () => { drag.current = null; };

  return createPortal(
    <div className="fixed inset-0 z-[200] flex flex-col bg-wp-ink/90 backdrop-blur-sm" data-testid="gallery-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between p-4 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm font-medium">{index + 1} / {pics.length}</span>
        <div className="flex items-center gap-1.5">
          <button onClick={zoomOut} disabled={scale <= 1} className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 disabled:opacity-40" data-testid="lightbox-zoom-out" aria-label="Zoom out"><Minus className="h-4 w-4" /></button>
          <button onClick={zoomIn} disabled={scale >= 3} className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25 disabled:opacity-40" data-testid="lightbox-zoom-in" aria-label="Zoom in"><Plus className="h-4 w-4" /></button>
          <button onClick={reset} className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25" data-testid="lightbox-reset" aria-label="Reset zoom"><RotateCcw className="h-4 w-4" /></button>
          <button onClick={onClose} className="grid h-9 w-9 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25" data-testid="lightbox-close" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()} onDoubleClick={() => (scale > 1 ? reset() : setScale(2))}
        onMouseDown={(e) => start(e.clientX, e.clientY)} onMouseMove={(e) => move(e.clientX, e.clientY)} onMouseUp={end} onMouseLeave={end}
        onTouchStart={(e) => e.touches[1] ? null : start(e.touches[0].clientX, e.touches[0].clientY)}
        onTouchMove={(e) => e.touches[1] ? null : move(e.touches[0].clientX, e.touches[0].clientY)} onTouchEnd={end}>
        <img src={pics[index]} alt={alt} draggable={false}
          onClick={() => scale === 1 && setScale(2)}
          style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`, cursor: scale > 1 ? "grab" : "zoom-in", touchAction: scale > 1 ? "none" : "auto" }}
          className="mx-auto h-full w-full select-none object-contain transition-transform duration-150 will-change-transform" />

        {pics.length > 1 && (
          <>
            <button onClick={() => onIndex((index - 1 + pics.length) % pics.length)} className="absolute left-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25" data-testid="lightbox-prev" aria-label="Previous"><ChevronLeft className="h-6 w-6" /></button>
            <button onClick={() => onIndex((index + 1) % pics.length)} className="absolute right-3 top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/15 text-white transition hover:bg-white/25" data-testid="lightbox-next" aria-label="Next"><ChevronRight className="h-6 w-6" /></button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
